import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type CrossCurricularTableColumnKey = 'seq' | 'topic_name' | 'body';

export type CrossCurricularTableRow = {
  _localId: string;
  id: string | null;
  topic_name: string;
  body: string;
  sort_order: number;
};

type CellPos = { row: number; col: number };

type Props = {
  topicName: string;
  rows: CrossCurricularTableRow[];
  saving?: boolean;
  onSave: (rows: CrossCurricularTableRow[]) => Promise<void>;
  showInlineActionButtons?: boolean;
  onBindActions?: (actions: {
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
  } | null) => void;
  onActionStateChange?: (state: { canUndo: boolean; canRedo: boolean; canSave: boolean }) => void;
  /** 표 진입 포커스(순 열)를 부모에 노출 */
  onBindFocusTable?: (focusTable: (() => void) | null) => void;
  /** 순 열에서 ← 시 주제어 섹션으로 복귀 */
  onLeaveToTopics?: () => void;
};

function comparableRows(rows: CrossCurricularTableRow[]): string {
  return JSON.stringify(
    rows.map((r) => ({ id: r.id, topic_name: r.topic_name, body: r.body, sort_order: r.sort_order }))
  );
}

const COLUMNS: Array<{ key: CrossCurricularTableColumnKey; label: string }> = [
  { key: 'seq', label: '순' },
  { key: 'topic_name', label: '주제어' },
  { key: 'body', label: '학습내용' },
];

const BODY_COL_INDEX = Math.max(
  0,
  COLUMNS.findIndex((c) => c.key === 'body')
);
const SEQ_COL_INDEX = Math.max(
  0,
  COLUMNS.findIndex((c) => c.key === 'seq')
);

function getCellDisplayValue(
  row: CrossCurricularTableRow,
  key: CrossCurricularTableColumnKey,
  rowIndex: number
): string {
  if (key === 'seq') return String(rowIndex + 1);
  return row[key];
}

function newLocalId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyCrossCurricularRow(
  topicName: string,
  sortOrder = 0
): CrossCurricularTableRow {
  return {
    _localId: newLocalId(),
    id: null,
    topic_name: topicName,
    body: '',
    sort_order: sortOrder,
  };
}

function parseExcelTsv(raw: string): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const text = raw.replace(/^\uFEFF/, '');
  const pushCell = () => {
    row.push(cell);
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some((v) => v.length > 0)) out.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] ?? '';
    const next = text[i + 1] ?? '';
    if (ch === '"') {
      const atCellStart = cell.length === 0;
      if (!inQuotes && !atCellStart) {
        cell += ch;
        continue;
      }
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && ch === '\t') {
      pushCell();
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && next === '\n') i += 1;
      pushRow();
      continue;
    }
    cell += ch;
  }
  if (cell.length > 0 || row.length > 0) pushRow();
  return out;
}

function normalizeTsvForCutCompare(raw: string): string {
  return raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/^\uFEFF/, '');
}

function normalizeRows(rows: CrossCurricularTableRow[], topicName: string): CrossCurricularTableRow[] {
  return rows.map((row, idx) => ({
    ...row,
    topic_name: row.topic_name.trim() || topicName,
    sort_order: idx,
  }));
}

const AdminCrossCurricularContentTable: React.FC<Props> = ({
  topicName,
  rows,
  saving = false,
  onSave,
  showInlineActionButtons = true,
  onBindActions,
  onActionStateChange,
  onBindFocusTable,
  onLeaveToTopics,
}) => {
  const [draftRows, setDraftRows] = useState<CrossCurricularTableRow[]>(rows);
  const [anchorCell, setAnchorCell] = useState<CellPos | null>(null);
  const [focusCell, setFocusCell] = useState<CellPos | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number; value: string } | null>(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [copiedSourceRect, setCopiedSourceRect] = useState<{
    minRow: number;
    maxRow: number;
    minCol: number;
    maxCol: number;
  } | null>(null);
  const [undoStack, setUndoStack] = useState<CrossCurricularTableRow[][]>([]);
  const [redoStack, setRedoStack] = useState<CrossCurricularTableRow[][]>([]);
  const [busySave, setBusySave] = useState(false);
  const pendingCutRef = useRef<{
    rect: { minRow: number; maxRow: number; minCol: number; maxCol: number };
    tsvNormalized: string;
  } | null>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const baselineComparableRef = useRef(comparableRows(rows));
  const undoActionRef = useRef<() => void>(() => undefined);
  const redoActionRef = useRef<() => void>(() => undefined);
  const saveActionRef = useRef<() => Promise<void>>(async () => undefined);

  useEffect(() => {
    setDraftRows(rows);
    baselineComparableRef.current = comparableRows(rows);
    setEditingCell(null);
    setUndoStack([]);
    setRedoStack([]);
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
  }, [rows]);

  const isDirty = comparableRows(draftRows) !== baselineComparableRef.current;

  const pushUndoSnapshot = (current: CrossCurricularTableRow[]) => {
    setUndoStack((prev) => [...prev, current.map((row) => ({ ...row }))]);
    setRedoStack([]);
  };

  const updateRows = (updater: (prev: CrossCurricularTableRow[]) => CrossCurricularTableRow[]) => {
    setDraftRows((prev) => {
      const prevClone = prev.map((row) => ({ ...row }));
      pushUndoSnapshot(prevClone);
      return normalizeRows(updater(prevClone), topicName);
    });
  };

  const selectedRect = useMemo(() => {
    if (!anchorCell || !focusCell) return null;
    return {
      minRow: Math.min(anchorCell.row, focusCell.row),
      maxRow: Math.max(anchorCell.row, focusCell.row),
      minCol: Math.min(anchorCell.col, focusCell.col),
      maxCol: Math.max(anchorCell.col, focusCell.col),
    };
  }, [anchorCell, focusCell]);

  const selectedRowsSorted = useMemo(() => {
    if (!selectedRect) return [] as number[];
    const rowsIdx: number[] = [];
    for (let r = selectedRect.minRow; r <= selectedRect.maxRow; r += 1) rowsIdx.push(r);
    return rowsIdx;
  }, [selectedRect]);

  const hasSelectedRows = selectedRowsSorted.length > 0;
  const hasSelection = !!selectedRect;
  const selectedCellCount = useMemo(() => {
    if (!selectedRect) return 0;
    return (
      (selectedRect.maxRow - selectedRect.minRow + 1) * (selectedRect.maxCol - selectedRect.minCol + 1)
    );
  }, [selectedRect]);

  const setSelection = (nextAnchor: CellPos, nextFocus: CellPos) => {
    setAnchorCell(nextAnchor);
    setFocusCell(nextFocus);
  };

  const isSelectedCell = (row: number, col: number): boolean => {
    if (!selectedRect) return false;
    return (
      row >= selectedRect.minRow &&
      row <= selectedRect.maxRow &&
      col >= selectedRect.minCol &&
      col <= selectedRect.maxCol
    );
  };

  const isCopiedSourceCell = (row: number, col: number): boolean => {
    if (!copiedSourceRect) return false;
    return (
      row >= copiedSourceRect.minRow &&
      row <= copiedSourceRect.maxRow &&
      col >= copiedSourceRect.minCol &&
      col <= copiedSourceRect.maxCol
    );
  };

  const startEditCell = (row: number, col: number, seed?: string) => {
    const key = COLUMNS[col]?.key;
    if (!key || key === 'seq') return;
    setEditingCell({ row, col, value: seed ?? getCellDisplayValue(draftRows[row]!, key, row) });
  };

  const commitEditingCell = () => {
    if (!editingCell) return;
    const key = COLUMNS[editingCell.col]?.key;
    if (!key || key === 'seq') {
      setEditingCell(null);
      return;
    }
    setDraftRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const before = next[editingCell.row]?.[key] ?? '';
      if (before === editingCell.value) return prev;
      pushUndoSnapshot(prev.map((row) => ({ ...row })));
      if (next[editingCell.row]) {
        if (key === 'topic_name') {
          const name = editingCell.value;
          for (const row of next) row.topic_name = name;
        } else {
          next[editingCell.row][key] = editingCell.value;
        }
      }
      return normalizeRows(next, topicName);
    });
    setEditingCell(null);
  };

  const handleAddRow = () => {
    updateRows((prev) => {
      const insertAt = selectedRowsSorted.length
        ? selectedRowsSorted[selectedRowsSorted.length - 1] + 1
        : prev.length;
      const row = createEmptyCrossCurricularRow(topicName, insertAt);
      return [...prev.slice(0, insertAt), row, ...prev.slice(insertAt)];
    });
    const row = selectedRowsSorted.length
      ? selectedRowsSorted[selectedRowsSorted.length - 1] + 1
      : draftRows.length;
    const next = { row: Math.max(0, row), col: focusCell?.col ?? BODY_COL_INDEX };
    setSelection(next, next);
  };

  const handleDeleteRows = () => {
    if (!hasSelectedRows) return;
    updateRows((prev) => prev.filter((_, idx) => !selectedRowsSorted.includes(idx)));
    const nextRow = Math.max(0, (selectedRowsSorted[0] ?? 0) - 1);
    setSelection({ row: nextRow, col: focusCell?.col ?? 0 }, { row: nextRow, col: focusCell?.col ?? 0 });
    setEditingCell(null);
  };

  const handleMoveRows = (direction: -1 | 1) => {
    if (!hasSelectedRows) return;
    updateRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const ordered = direction === -1 ? [...selectedRowsSorted] : [...selectedRowsSorted].reverse();
      for (const idx of ordered) {
        const swapWith = idx + direction;
        if (swapWith < 0 || swapWith >= next.length) continue;
        if (selectedRowsSorted.includes(swapWith)) continue;
        const tmp = next[idx];
        next[idx] = next[swapWith];
        next[swapWith] = tmp;
      }
      return next;
    });
    if (anchorCell && focusCell) {
      setSelection(
        { row: anchorCell.row + direction, col: anchorCell.col },
        { row: focusCell.row + direction, col: focusCell.col }
      );
    }
  };

  const handleClearData = () => {
    if (!selectedRect) return;
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
    updateRows((prev) =>
      prev.map((row, idx) => {
        if (idx < selectedRect.minRow || idx > selectedRect.maxRow) return row;
        const nextRow = { ...row };
        for (let col = selectedRect.minCol; col <= selectedRect.maxCol; col += 1) {
          const key = COLUMNS[col]?.key;
          if (key && key !== 'seq') nextRow[key] = '';
        }
        return nextRow;
      })
    );
  };

  const copyRowsAsTsv = async (cut: boolean) => {
    if (!selectedRect) return;
    const lines: string[] = [];
    for (let r = selectedRect.minRow; r <= selectedRect.maxRow; r += 1) {
      const cells: string[] = [];
      for (let c = selectedRect.minCol; c <= selectedRect.maxCol; c += 1) {
        const key = COLUMNS[c]?.key;
        const value = key
          ? getCellDisplayValue(draftRows[r]!, key, r).replace(/\t/g, ' ')
          : '';
        cells.push(value);
      }
      lines.push(cells.join('\t'));
    }
    const body = lines.join('\n');
    await navigator.clipboard.writeText(body);
    const rect = { ...selectedRect };
    setCopiedSourceRect(rect);
    pendingCutRef.current = cut ? { rect, tsvNormalized: normalizeTsvForCutCompare(body) } : null;
  };

  const handlePaste = async () => {
    if (!focusCell && !selectedRect) return;
    const raw = await navigator.clipboard.readText();
    if (!raw.trim()) return;
    const clipNorm = normalizeTsvForCutCompare(raw);
    const pendingCut = pendingCutRef.current;
    const cutRect = pendingCut && clipNorm === pendingCut.tsvNormalized ? pendingCut.rect : null;
    const grid = parseExcelTsv(raw);
    if (!grid.length) return;

    const startRow = selectedRect?.minRow ?? focusCell!.row;
    const startCol = selectedRect?.minCol ?? focusCell!.col;

    updateRows((prev) => {
      let next = prev.map((row) => ({ ...row }));
      while (next.length < startRow + grid.length) {
        next.push(createEmptyCrossCurricularRow(topicName, next.length));
      }
      for (let r = 0; r < grid.length; r += 1) {
        const rowVals = grid[r] ?? [];
        const targetRow = startRow + r;
        if (!next[targetRow]) next[targetRow] = createEmptyCrossCurricularRow(topicName, targetRow);
        for (let c = 0; c < rowVals.length; c += 1) {
          const colIndex = startCol + c;
          const key = COLUMNS[colIndex]?.key;
          if (!key || key === 'seq') continue;
          next[targetRow][key] = rowVals[c] ?? '';
        }
        // 엑셀에서 주제어+학습내용 2열만 복사해 순 열부터 붙이는 경우
        if (rowVals.length === 2 && startCol === 0) {
          next[targetRow].topic_name = rowVals[0] ?? next[targetRow].topic_name;
          next[targetRow].body = rowVals[1] ?? '';
        } else if (rowVals.length === 1 && COLUMNS[startCol]?.key === 'body') {
          next[targetRow].body = rowVals[0] ?? '';
        }
      }
      if (selectedRect && grid.length === 1 && grid[0].length === 1) {
        const fill = grid[0][0] ?? '';
        for (let r = selectedRect.minRow; r <= selectedRect.maxRow; r += 1) {
          for (let c = selectedRect.minCol; c <= selectedRect.maxCol; c += 1) {
            const key = COLUMNS[c]?.key;
            if (key && key !== 'seq' && next[r]) next[r][key] = fill;
          }
        }
      }
      if (cutRect) {
        next = next.map((row, idx) => {
          if (idx < cutRect.minRow || idx > cutRect.maxRow) return row;
          const copy = { ...row };
          for (let c = cutRect.minCol; c <= cutRect.maxCol; c += 1) {
            const key = COLUMNS[c]?.key;
            if (key && key !== 'seq') copy[key] = '';
          }
          return copy;
        });
      }
      const firstTopic = next.map((r) => r.topic_name.trim()).find(Boolean) || topicName;
      return next.map((r) => ({ ...r, topic_name: r.topic_name.trim() || firstTopic }));
    });
    pendingCutRef.current = null;
    setCopiedSourceRect(null);
  };

  const handleUndo = () => {
    setUndoStack((prev) => {
      if (!prev.length) return prev;
      const snapshot = prev[prev.length - 1];
      setRedoStack((redo) => [...redo, draftRows.map((r) => ({ ...r }))]);
      setDraftRows(snapshot.map((r) => ({ ...r })));
      return prev.slice(0, -1);
    });
  };

  const handleRedo = () => {
    setRedoStack((prev) => {
      if (!prev.length) return prev;
      const snapshot = prev[prev.length - 1];
      setUndoStack((undo) => [...undo, draftRows.map((r) => ({ ...r }))]);
      setDraftRows(snapshot.map((r) => ({ ...r })));
      return prev.slice(0, -1);
    });
  };

  const handleSave = async () => {
    if (busySave || saving || !isDirty) return;
    setBusySave(true);
    try {
      const normalized = normalizeRows(
        draftRows.map((r) => ({ ...r })),
        topicName
      );
      await onSave(normalized);
      baselineComparableRef.current = comparableRows(normalized);
      setDraftRows(normalized);
      setUndoStack([]);
      setRedoStack([]);
    } finally {
      setBusySave(false);
    }
  };

  undoActionRef.current = handleUndo;
  redoActionRef.current = handleRedo;
  saveActionRef.current = handleSave;

  useEffect(() => {
    if (!onBindActions) return;
    onBindActions({
      undo: () => undoActionRef.current(),
      redo: () => redoActionRef.current(),
      save: () => saveActionRef.current(),
    });
    return () => onBindActions(null);
  }, [onBindActions]);

  const focusTableEntry = useCallback(() => {
    const rowCount = draftRows.length;
    if (rowCount <= 0) {
      tableWrapRef.current?.focus();
      return;
    }
    const row = Math.max(0, Math.min(rowCount - 1, focusCell?.row ?? 0));
    const next = { row, col: SEQ_COL_INDEX };
    setSelection(next, next);
    window.requestAnimationFrame(() => {
      tableWrapRef.current?.focus();
      const cell = tableWrapRef.current?.querySelector(
        `td[data-row-index="${row}"][data-col-index="${SEQ_COL_INDEX}"]`
      ) as HTMLElement | null;
      cell?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }, [draftRows.length, focusCell?.row]);

  const focusTableEntryRef = useRef(focusTableEntry);
  focusTableEntryRef.current = focusTableEntry;

  useEffect(() => {
    if (!onBindFocusTable) return;
    onBindFocusTable(() => focusTableEntryRef.current());
    return () => onBindFocusTable(null);
  }, [onBindFocusTable]);

  useEffect(() => {
    onActionStateChange?.({
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
      canSave: !saving && !busySave && isDirty,
    });
  }, [busySave, isDirty, onActionStateChange, redoStack.length, saving, undoStack.length]);

  useEffect(() => {
    if (!isDraggingSelection) return;
    const onMove = (event: MouseEvent) => {
      if (!anchorCell) return;
      const target = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
      const cellEl = target?.closest?.('td[data-admin-cell="1"]') as HTMLTableCellElement | null;
      if (!cellEl) return;
      const row = Number(cellEl.dataset.rowIndex ?? '');
      const col = Number(cellEl.dataset.colIndex ?? '');
      if (!Number.isInteger(row) || !Number.isInteger(col)) return;
      setFocusCell((prev) => (prev && prev.row === row && prev.col === col ? prev : { row, col }));
    };
    const onUp = () => setIsDraggingSelection(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [anchorCell, isDraggingSelection]);

  const handleTableKeyDown = (e: React.KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      void copyRowsAsTsv(false);
      return;
    }
    if (meta && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      void copyRowsAsTsv(true);
      return;
    }
    if (meta && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      void handlePaste();
      return;
    }
    if (meta && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) handleRedo();
      else handleUndo();
      return;
    }
    if (editingCell) {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEditingCell();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditingCell(null);
      }
      return;
    }
    if (!focusCell) return;
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      startEditCell(focusCell.row, focusCell.col);
      return;
    }
    if (e.key.length === 1 && !meta) {
      e.preventDefault();
      startEditCell(focusCell.row, focusCell.col, e.key);
      return;
    }
    const move = (dr: number, dc: number) => {
      const row = Math.max(0, Math.min(draftRows.length - 1, focusCell.row + dr));
      const col = Math.max(0, Math.min(COLUMNS.length - 1, focusCell.col + dc));
      const next = { row, col };
      if (e.shiftKey && anchorCell) setSelection(anchorCell, next);
      else setSelection(next, next);
    };
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1, 0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1, 0);
    } else if (e.key === 'ArrowLeft') {
      if (
        !e.shiftKey &&
        focusCell.col === SEQ_COL_INDEX &&
        onLeaveToTopics
      ) {
        e.preventDefault();
        onLeaveToTopics();
        return;
      }
      e.preventDefault();
      move(0, -1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      move(0, 1);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      handleClearData();
    }
  };

  return (
    <section className="cp-admin-learning-table-shell cp-program-progress-table-wrap cp-admin-cross-table-shell">
      <div className="cp-program-progress-toolbar">
        <div className="cp-program-progress-toolbar-trailing cp-admin-cross-toolbar-trailing">
          <div className="cp-program-progress-toolbar-group" role="group" aria-label="행 편집 도구">
            <button type="button" className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus" onClick={handleAddRow}>
              <span className="cp-btn-label-desktop">행 추가</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                +
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
              onClick={handleDeleteRows}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">행 삭제</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                -
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region"
              onClick={() => handleMoveRows(-1)}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">위로 올리기</span>
              <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                  <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                  <path
                    d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                    fill="currentColor"
                    opacity="0.45"
                  />
                </svg>
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-swap-region"
              onClick={() => handleMoveRows(1)}
              disabled={!hasSelectedRows}
            >
              <span className="cp-btn-label-desktop">아래로 내리기</span>
              <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                  <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                  <path
                    d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                    fill="currentColor"
                    opacity="0.45"
                  />
                </svg>
              </span>
            </button>
          </div>
          <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드">
            <button type="button" className="cp-btn-copy-paste" onClick={() => void copyRowsAsTsv(false)} disabled={!hasSelection}>
              <span className="cp-btn-label-desktop">복사</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button type="button" className="cp-btn-copy-paste" onClick={() => void copyRowsAsTsv(true)} disabled={!hasSelection}>
              <span className="cp-btn-label-desktop">잘라내기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste"
              onClick={() => void handlePaste()}
              disabled={!focusCell && !selectedRect}
            >
              <span className="cp-btn-label-desktop">붙여넣기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button type="button" className="cp-btn-copy-paste" onClick={handleClearData} disabled={!hasSelection}>
              <span className="cp-btn-label-desktop">지우기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            {showInlineActionButtons ? (
              <>
                <button type="button" className="cp-btn-copy-paste" onClick={handleUndo} disabled={!undoStack.length}>
                  <span className="cp-btn-label-desktop">실행취소</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3 7v6h6M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                <button type="button" className="cp-btn-copy-paste" onClick={handleRedo} disabled={!redoStack.length}>
                  <span className="cp-btn-label-desktop">다시실행</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M21 7v6h-6M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              </>
            ) : null}
          </div>
          {showInlineActionButtons ? (
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => void handleSave()}
              disabled={saving || busySave || !isDirty}
            >
              {saving || busySave ? '저장 중…' : '저장'}
            </button>
          ) : null}
        </div>
      </div>

      <div
        ref={tableWrapRef}
        className="cp-admin-learning-table-wrap"
        tabIndex={0}
        onKeyDown={handleTableKeyDown}
        role="grid"
        aria-label="범교과 학습내용 표"
      >
        <table className="cp-annual-progress-table cp-admin-learning-table cp-admin-cross-content-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draftRows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="cp-admin-cross-table-empty">
                  행 추가 또는 엑셀에서 붙여넣기로 학습내용을 입력하세요.
                </td>
              </tr>
            ) : (
              draftRows.map((row, rowIndex) => (
                <tr key={row._localId}>
                  {COLUMNS.map((col, colIndex) => {
                    const selected = isSelectedCell(rowIndex, colIndex);
                    const single = selected && selectedCellCount === 1;
                    const copied = isCopiedSourceCell(rowIndex, colIndex);
                    const copiedSourceSingle =
                      !!copiedSourceRect &&
                      copied &&
                      copiedSourceRect.minRow === copiedSourceRect.maxRow &&
                      copiedSourceRect.minCol === copiedSourceRect.maxCol;
                    const copiedSourceMulti = !!copiedSourceRect && copied && !copiedSourceSingle;
                    const editing =
                      editingCell?.row === rowIndex && editingCell?.col === colIndex;
                    const edgeTop = !!selectedRect && rowIndex === selectedRect.minRow;
                    const edgeBottom = !!selectedRect && rowIndex === selectedRect.maxRow;
                    const edgeLeft = !!selectedRect && colIndex === selectedRect.minCol;
                    const edgeRight = !!selectedRect && colIndex === selectedRect.maxCol;
                    const copiedEdgeTop = !!copiedSourceRect && rowIndex === copiedSourceRect.minRow;
                    const copiedEdgeBottom = !!copiedSourceRect && rowIndex === copiedSourceRect.maxRow;
                    const copiedEdgeLeft = !!copiedSourceRect && colIndex === copiedSourceRect.minCol;
                    const copiedEdgeRight = !!copiedSourceRect && colIndex === copiedSourceRect.maxCol;
                    const classNames = [
                      'cp-program-progress-editable-cell',
                      col.key === 'seq'
                        ? 'cp-admin-cross-col-seq'
                        : col.key === 'body'
                          ? 'cp-admin-cross-col-body'
                          : 'cp-admin-cross-col-topic',
                      selected
                        ? single
                          ? 'cp-base-timetable-cell-selected-single'
                          : 'cp-base-timetable-cell-selected-multi'
                        : '',
                      selected && !single && edgeTop ? 'cp-base-timetable-cell-edge-top' : '',
                      selected && !single && edgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
                      selected && !single && edgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
                      selected && !single && edgeRight ? 'cp-base-timetable-cell-edge-right' : '',
                      copied ? 'cp-base-timetable-cell-copied-source' : '',
                      copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
                      copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
                      copiedSourceMulti && copiedEdgeTop ? 'cp-base-timetable-cell-copied-edge-top' : '',
                      copiedSourceMulti && copiedEdgeBottom ? 'cp-base-timetable-cell-copied-edge-bottom' : '',
                      copiedSourceMulti && copiedEdgeLeft ? 'cp-base-timetable-cell-copied-edge-left' : '',
                      copiedSourceMulti && copiedEdgeRight ? 'cp-base-timetable-cell-copied-edge-right' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <td
                        key={col.key}
                        data-admin-cell="1"
                        data-row-index={rowIndex}
                        data-col-index={colIndex}
                        className={classNames}
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          if (editing) return;
                          e.preventDefault();
                          tableWrapRef.current?.focus();
                          const pos = { row: rowIndex, col: colIndex };
                          if (e.shiftKey && anchorCell) setSelection(anchorCell, pos);
                          else setSelection(pos, pos);
                          setIsDraggingSelection(true);
                        }}
                        onMouseEnter={() => {
                          if (!isDraggingSelection || !anchorCell) return;
                          setFocusCell({ row: rowIndex, col: colIndex });
                        }}
                        onDoubleClick={() => startEditCell(rowIndex, colIndex)}
                      >
                        {editing ? (
                          <input
                            className="cp-program-progress-cell-input cp-admin-learning-cell-input"
                            value={editingCell.value}
                            autoFocus
                            onChange={(e) =>
                              setEditingCell((prev) => (prev ? { ...prev, value: e.target.value } : prev))
                            }
                            onBlur={() => commitEditingCell()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitEditingCell();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setEditingCell(null);
                              }
                            }}
                          />
                        ) : (
                          <div className="cp-admin-learning-cell-text">
                            {getCellDisplayValue(row, col.key, rowIndex)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AdminCrossCurricularContentTable;
